import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { 
  Code2, Award, Clock, ChevronRight, ChevronLeft, Sun, Moon, 
  ArrowLeft, CheckCircle, XCircle, Trophy, User, LogOut, AlertTriangle, Shuffle
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { examsApi, coursesApi } from '../lib/api';
import { toast } from 'sonner';

// Per-question time limit in seconds
const QUESTION_TIME_LIMIT = 60;

export default function ExamPage() {
  const { courseId } = useParams();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  
  const [course, setCourse] = useState(null);
  const [examData, setExamData] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [totalTimeRemaining, setTotalTimeRemaining] = useState(null);
  const [questionTimeRemaining, setQuestionTimeRemaining] = useState(QUESTION_TIME_LIMIT);
  const [questionTimerActive, setQuestionTimerActive] = useState(true);
  const [shuffledOptions, setShuffledOptions] = useState({}); // Store shuffled indices per question

  // Function to shuffle options for current question
  const shuffleCurrentOptions = () => {
    if (!examData?.questions || examData.questions.length === 0) return;
    
    const currentQ = examData.questions[currentQuestion];
    if (!currentQ?.options) return;
    
    // Create shuffled indices
    const indices = currentQ.options.map((_, i) => i);
    for (let i = indices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [indices[i], indices[j]] = [indices[j], indices[i]];
    }
    
    // Update shuffled options for this question
    setShuffledOptions(prev => ({
      ...prev,
      [currentQuestion]: indices
    }));
    
    // Update the answer if already selected (map to new index)
    if (answers[currentQuestion] !== undefined) {
      const oldAnswer = answers[currentQuestion];
      const newAnswer = indices.indexOf(oldAnswer);
      if (newAnswer !== -1) {
        const newAnswers = [...answers];
        newAnswers[currentQuestion] = newAnswer;
        setAnswers(newAnswers);
      }
    }
    
    toast.success('Options shuffled!');
  };

  // Get display options (shuffled or original)
  const getDisplayOptions = () => {
    const currentQ = examData?.questions?.[currentQuestion];
    if (!currentQ?.options) return [];
    
    const indices = shuffledOptions[currentQuestion];
    if (!indices) return currentQ.options;
    
    return indices.map(i => currentQ.options[i]);
  };

  // Map selected index back to original index for submission
  const getOriginalIndex = (displayIndex) => {
    const indices = shuffledOptions[currentQuestion];
    if (!indices) return displayIndex;
    return indices[displayIndex];
  };

  useEffect(() => {
    fetchExamData();
  }, [courseId]);

  // Total exam timer
  useEffect(() => {
    if (totalTimeRemaining === null || result) return;
    
    const timer = setInterval(() => {
      setTotalTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmitExam();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [totalTimeRemaining, result]);

  // Per-question timer
  useEffect(() => {
    if (!questionTimerActive || result || !examData) return;
    
    const timer = setInterval(() => {
      setQuestionTimeRemaining(prev => {
        if (prev <= 1) {
          // Auto-advance to next question or submit if last
          if (currentQuestion < (examData?.questions?.length || 1) - 1) {
            setCurrentQuestion(curr => curr + 1);
            setQuestionTimeRemaining(QUESTION_TIME_LIMIT);
            toast.info('Time up! Moving to next question.');
          } else {
            // Last question - auto submit
            handleSubmitExam();
          }
          return QUESTION_TIME_LIMIT;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [questionTimerActive, currentQuestion, result, examData]);

  // Reset question timer when changing questions
  useEffect(() => {
    setQuestionTimeRemaining(QUESTION_TIME_LIMIT);
  }, [currentQuestion]);

  const fetchExamData = async () => {
    try {
      setLoading(true);
      const courseData = await coursesApi.getById(courseId);
      setCourse(courseData);
      
      const exam = await examsApi.start(courseId);
      setExamData(exam);
      const questionCount = exam.questions?.length || 0;
      setAnswers(new Array(questionCount).fill(-1));
      setTotalTimeRemaining(exam.time_limit_minutes * 60);
      setQuestionTimeRemaining(QUESTION_TIME_LIMIT);
      setQuestionTimerActive(true);
    } catch (error) {
      console.error('Failed to start exam:', error);
      toast.error(error.message || 'Failed to start exam');
      navigate(`/lab/${courseId}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = (questionIndex, answerIndex) => {
    const newAnswers = [...answers];
    newAnswers[questionIndex] = answerIndex;
    setAnswers(newAnswers);
  };

  // Handle confirming answer and moving to next question
  const handleConfirmAnswer = () => {
    if (answers[currentQuestion] === -1) {
      toast.warning('Please select an answer first');
      return;
    }
    
    if (currentQuestion < (examData?.questions?.length || 1) - 1) {
      setCurrentQuestion(currentQuestion + 1);
      toast.success('Answer saved! Moving to next question.');
    } else {
      // Last question - show submit dialog
      setShowSubmitDialog(true);
    }
  };

  const handleSubmitExam = async () => {
    setShowSubmitDialog(false);
    setSubmitting(true);
    
    try {
      const examResult = await examsApi.submit(courseId, examData.exam_id, answers);
      setResult(examResult);
      
      if (examResult.passed) {
        toast.success('Congratulations! You passed the exam!');
      } else {
        toast.error('You did not pass. Review the material and try again.');
      }
    } catch (error) {
      toast.error('Failed to submit exam');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const answeredCount = answers?.filter(a => a !== -1).length || 0;
  const progressPercent = (answeredCount / (examData?.questions?.length || 1)) * 100;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Loading exam...</p>
        </div>
      </div>
    );
  }

  // Show result screen
  if (result) {
    return (
      <div className="min-h-screen bg-background" data-testid="exam-result">
        {/* Background */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="gradient-blob w-96 h-96 bg-primary/20 -top-48 -right-48" />
          <div className="gradient-blob w-64 h-64 bg-secondary/20 bottom-0 left-1/4" />
        </div>

        {/* Header */}
        <header className="relative z-10 glass border-b border-border/50">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <Link to="/dashboard" className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                  <Code2 className="w-6 h-6 text-white" />
                </div>
                <span className="text-xl font-bold">CognitiaIndia</span>
              </Link>
            </div>
          </div>
        </header>

        {/* Result Content */}
        <main className="relative z-10 max-w-2xl mx-auto px-6 py-16">
          <div className="text-center">
            <div className={`w-24 h-24 mx-auto rounded-full flex items-center justify-center mb-6 ${result.passed ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
              {result.passed ? (
                <Trophy className="w-12 h-12 text-green-500" />
              ) : (
                <XCircle className="w-12 h-12 text-red-500" />
              )}
            </div>

            <h1 className="text-3xl font-bold mb-2">
              {result.passed ? 'Congratulations!' : 'Keep Learning!'}
            </h1>
            <p className="text-muted-foreground mb-8">
              {result.message}
            </p>

            <div className="p-8 rounded-xl bg-card border border-border mb-8">
              <div className="grid grid-cols-3 gap-6 text-center">
                <div>
                  <div className="text-4xl font-bold text-primary">{Math.round(result.score)}%</div>
                  <div className="text-sm text-muted-foreground">Your Score</div>
                </div>
                <div>
                  <div className="text-4xl font-bold">{result.correct}/{result.total}</div>
                  <div className="text-sm text-muted-foreground">Correct Answers</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-amber-500">{result.passing_score}%</div>
                  <div className="text-sm text-muted-foreground">Passing Score</div>
                </div>
              </div>
            </div>

            <div className="flex gap-4 justify-center">
              {result.passed ? (
                <>
                  <Button onClick={() => navigate('/courses')} className="btn-primary rounded-full">
                    <ChevronRight className="w-4 h-4 mr-2" />
                    Explore More Courses
                  </Button>
                  <Button variant="outline" onClick={() => navigate('/dashboard')} className="rounded-full">
                    Go to Dashboard
                  </Button>
                </>
              ) : (
                <>
                  <Button onClick={() => navigate(`/lab/${courseId}`)} className="btn-primary rounded-full">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Review Material
                  </Button>
                  <Button variant="outline" onClick={() => fetchExamData()} className="rounded-full">
                    Retry Exam
                  </Button>
                </>
              )}
            </div>
          </div>
        </main>
      </div>
    );
  }

  const currentQ = examData?.questions?.[currentQuestion];

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="exam-page">
      {/* Header */}
      <header className="z-10 glass border-b border-border/50">
        <div className="px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                  <Code2 className="w-5 h-5 text-white" />
                </div>
                <span className="font-bold hidden sm:block">CognitiaIndia</span>
              </Link>

              <div className="flex items-center gap-2">
                <Award className="w-5 h-5 text-primary" />
                <span className="font-semibold">{course?.title} - Final Exam</span>
              </div>
            </div>

            <div className="flex items-center gap-4">
              {/* Per-Question Timer */}
              <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${questionTimeRemaining <= 10 ? 'bg-red-500/20 text-red-500 animate-pulse' : 'bg-amber-500/20 text-amber-500'}`}>
                <Clock className="w-4 h-4" />
                <span className="font-mono font-bold text-sm">{questionTimeRemaining}s</span>
                <span className="text-xs opacity-70">Q{currentQuestion + 1}</span>
              </div>

              {/* Total Exam Timer */}
              <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${totalTimeRemaining < 300 ? 'bg-red-500/20 text-red-500' : 'bg-muted'}`}>
                <Clock className="w-5 h-5" />
                <span className="font-mono font-bold">{formatTime(totalTimeRemaining)}</span>
              </div>

              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="rounded-full"
              >
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      {user?.picture ? (
                        <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                      ) : (
                        <User className="w-4 h-4 text-primary" />
                      )}
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => navigate('/dashboard')}>
                    Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="px-6 py-3 border-b border-border bg-muted/30">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-muted-foreground">
            Question {currentQuestion + 1} of {examData?.questions?.length}
          </span>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span>{answeredCount} of {examData?.questions?.length} answered</span>
            <span className="text-xs px-2 py-1 bg-amber-500/20 text-amber-500 rounded">
              {QUESTION_TIME_LIMIT}s per question
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          <Progress value={progressPercent} className="h-2 flex-1" />
          <Progress value={(questionTimeRemaining / QUESTION_TIME_LIMIT) * 100} className="h-2 w-24 bg-amber-500/20" />
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 flex">
        {/* Question Navigator */}
        <div className="w-20 border-r border-border bg-card p-3">
          <div className="grid grid-cols-2 gap-2">
            {examData?.questions?.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentQuestion(index)}
                className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                  currentQuestion === index
                    ? 'bg-primary text-white'
                    : answers[index] !== -1
                    ? 'bg-green-500/20 text-green-500 border border-green-500/30'
                    : 'bg-muted hover:bg-muted/80'
                }`}
                data-testid={`question-nav-${index}`}
              >
                {index + 1}
              </button>
            ))}
          </div>
        </div>

        {/* Question Content */}
        <div className="flex-1 p-8">
          <div className="max-w-3xl mx-auto">
            <div className="mb-8">
              <span className="text-sm font-medium text-primary mb-2 block">
                Question {currentQuestion + 1}
              </span>
              {/* Render question with code snippets */}
              <div className="text-xl font-semibold" data-testid="question-text">
                {currentQ?.question?.includes('```') ? (
                  <div className="space-y-3">
                    {currentQ.question.split('```').map((part, idx) => {
                      if (idx % 2 === 0) {
                        // Regular text
                        return <p key={idx} className="whitespace-pre-wrap">{part.trim()}</p>;
                      } else {
                        // Code block
                        const lines = part.split('\n');
                        const language = lines[0].trim();
                        const code = lines.slice(1).join('\n').trim();
                        return (
                          <div key={idx} className="bg-slate-900 rounded-lg overflow-hidden">
                            <div className="bg-slate-800 px-4 py-2 text-xs text-slate-400 border-b border-slate-700">
                              {language || 'Code'}
                            </div>
                            <pre className="p-4 text-sm font-mono text-slate-100 overflow-x-auto">
                              <code>{code}</code>
                            </pre>
                          </div>
                        );
                      }
                    })}
                  </div>
                ) : (
                  <h2 className="whitespace-pre-wrap">{currentQ?.question}</h2>
                )}
              </div>
            </div>
            
            {/* Shuffle Button */}
            <div className="flex justify-end mb-3">
              <Button
                variant="outline"
                size="sm"
                onClick={shuffleCurrentOptions}
                className="gap-2"
                data-testid="shuffle-options-btn"
              >
                <Shuffle className="w-4 h-4" />
                Shuffle Options
              </Button>
            </div>

            <RadioGroup
              value={answers[currentQuestion]?.toString()}
              onValueChange={(value) => {
                const displayIndex = parseInt(value);
                const originalIndex = getOriginalIndex(displayIndex);
                handleAnswerSelect(currentQuestion, originalIndex);
              }}
              className="space-y-3"
            >
              {getDisplayOptions().map((option, index) => (
                <div
                  key={index}
                  className={`flex items-center space-x-3 p-4 rounded-lg border transition-colors cursor-pointer ${
                    answers[currentQuestion] === getOriginalIndex(index)
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => {
                    const originalIndex = getOriginalIndex(index);
                    handleAnswerSelect(currentQuestion, originalIndex);
                  }}
                  data-testid={`option-${index}`}
                >
                  <RadioGroupItem value={index.toString()} id={`option-${index}`} />
                  <Label htmlFor={`option-${index}`} className="flex-1 cursor-pointer text-base">
                    {option}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border bg-card p-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))}
            disabled={currentQuestion === 0}
            data-testid="prev-question-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>

          <div className="flex items-center gap-3">
            {/* Confirm Answer Button - allows early submission */}
            <Button
              onClick={handleConfirmAnswer}
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={answers[currentQuestion] === -1}
              data-testid="confirm-answer-btn"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Confirm & Next
            </Button>

            {currentQuestion < (examData?.questions?.length || 1) - 1 ? (
              <Button
                variant="outline"
                onClick={() => setCurrentQuestion(currentQuestion + 1)}
                data-testid="next-question-btn"
              >
                Skip
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={() => setShowSubmitDialog(true)}
                className="bg-green-600 hover:bg-green-700"
                disabled={submitting}
                data-testid="submit-exam-btn"
              >
                {submitting ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <Award className="w-4 h-4 mr-2" />
                )}
                Submit Exam
              </Button>
            )}
          </div>
        </div>
      </footer>

      {/* Submit Confirmation Dialog */}
      <AlertDialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Submit Exam?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {answeredCount < (examData?.questions?.length || 0) ? (
                <span className="text-amber-500">
                  You have {(examData?.questions?.length || 0) - answeredCount} unanswered question(s).
                </span>
              ) : (
                <span>You have answered all questions.</span>
              )}
              <br />
              Are you sure you want to submit your exam? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Review Answers</AlertDialogCancel>
            <AlertDialogAction onClick={handleSubmitExam} className="bg-green-600 hover:bg-green-700">
              Submit Exam
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
